import { Fragment } from 'react';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';

import { PropsWithClassName } from '@/types';
import { cn } from '@/utils';

type Props = PropsWithClassName<{
  currentPage: number;
  loadMore?: (pageNumber?: number) => void;
  className?: string;
  onPageChange: (selectedItem: { selected: number }) => void;
  pageCount: number;
  marginPagesDisplayed?: number;
  pageRangeDisplayed?: number;
  breakLabel?: string | React.ReactNode;
  usePageLabelBuilder?: boolean;
}>;

// Builds the list of (0-indexed) pages to render. The window around the
// current page is always exactly `pageRangeDisplayed` pages wide (shifted
// against the closest edge when the current page is near a boundary),
// unless there simply aren't enough pages in total.
// NOTE: react-paginate's own `pageRangeDisplayed` windowing has a bug for
// odd values (it renders one extra page when the current page is 1 or 2
// pages away from the start), so pages are computed manually instead.
const getVisiblePages = (
  currentPage: number,
  pageCount: number,
  pageRangeDisplayed: number,
  marginPagesDisplayed: number
): number[] => {
  if (pageCount <= pageRangeDisplayed) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const delta = Math.floor(pageRangeDisplayed / 2);
  let start = currentPage - delta;
  let end = currentPage + delta;

  if (start < 0) {
    end -= start;
    start = 0;
  }
  if (end > pageCount - 1) {
    start -= end - (pageCount - 1);
    end = pageCount - 1;
  }
  start = Math.max(0, start);
  end = Math.min(pageCount - 1, end);

  const windowPages = Array.from(
    { length: end - start + 1 },
    (_, index) => start + index
  );

  if (marginPagesDisplayed <= 0) {
    return windowPages;
  }

  const marginCount = Math.min(marginPagesDisplayed, pageCount);
  const leftMargin = Array.from({ length: marginCount }, (_, index) => index);
  const rightMargin = Array.from(
    { length: marginCount },
    (_, index) => pageCount - 1 - index
  ).reverse();

  return Array.from(
    new Set([...leftMargin, ...windowPages, ...rightMargin])
  ).sort((a, b) => a - b);
};

const handleKeyActivate =
  (action: () => void) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

export const PaginatorAsync = ({
  currentPage,
  onPageChange,
  pageCount,
  className,
  marginPagesDisplayed = 0,
  pageRangeDisplayed = 5,
  breakLabel = '',
  usePageLabelBuilder = false,
}: Props) => {
  if (pageCount <= 1) {
    return null;
  }

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pageCount - 1;
  const visiblePages = getVisiblePages(
    currentPage,
    pageCount,
    pageRangeDisplayed,
    marginPagesDisplayed
  );

  const goToPage = (page: number) => onPageChange({ selected: page });

  return (
    <ul
      aria-label="Pagination"
      className={cn('paginator-gdt w-full items-center', className)}
      role="navigation"
    >
      <li className={cn('previous', isFirstPage && 'disabled')}>
        <a
          aria-disabled={isFirstPage}
          aria-label="Previous page"
          role="button"
          tabIndex={isFirstPage ? -1 : 0}
          onClick={() => !isFirstPage && goToPage(currentPage - 1)}
          onKeyDown={handleKeyActivate(() => goToPage(currentPage - 1))}
        >
          <div className="flex items-center">
            <GoArrowLeft size={15} />
            <span className="pl-2">Anterior</span>
          </div>
        </a>
      </li>

      {visiblePages.map((page, index) => {
        const previousVisiblePage = visiblePages[index - 1];
        const hasGapBefore =
          previousVisiblePage !== undefined && page - previousVisiblePage > 1;
        const isSelected = page === currentPage;

        return (
          <Fragment key={page}>
            {hasGapBefore && <li>{breakLabel}</li>}
            <li className={cn('page-item', isSelected && 'active')}>
              <a
                aria-current={isSelected ? 'page' : undefined}
                aria-label={
                  isSelected
                    ? `Page ${page + 1} is your current page`
                    : `Page ${page + 1}`
                }
                role="button"
                tabIndex={isSelected ? -1 : 0}
                onClick={() => goToPage(page)}
                onKeyDown={handleKeyActivate(() => goToPage(page))}
              >
                {usePageLabelBuilder ? (
                  <span className="flex items-center gap-x-1">
                    <span>{page + 1}</span> de <span>{pageCount}</span>
                  </span>
                ) : (
                  page + 1
                )}
              </a>
            </li>
          </Fragment>
        );
      })}

      <li className={cn('page-item', isLastPage && 'disabled')}>
        <a
          aria-disabled={isLastPage}
          aria-label="Next page"
          role="button"
          tabIndex={isLastPage ? -1 : 0}
          onClick={() => !isLastPage && goToPage(currentPage + 1)}
          onKeyDown={handleKeyActivate(() => goToPage(currentPage + 1))}
        >
          <div className="flex items-center">
            <span className="pr-2">Siguiente</span>
            <GoArrowRight size={15} />
          </div>
        </a>
      </li>
    </ul>
  );
};
